import NiceModal from '@ebay/nice-modal-react'
import { LockOpenOutlined, LockOutlined } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Icon,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import dayjs from 'dayjs'
import React, { useState, useContext, useRef, useEffect } from 'react'
import { GlobalContext } from '../context/GlobalContext'
import useApi from '../hooks/useApi'
import { ConsumablesBox, DocumentChip, RegPlate, StatusChip } from './EnquiryItem'
import { ModalContainerAnimateDown } from './gsys-ui/ModalContainer/ModalContainer'
import haxios from '../lib/haxios'
import { getImageFromBlobId, sendMessage } from '../lib/api'
import { Spinner } from './gsys-ui/Spinner/Spinner'
import ComposeMessage from './ComposeMessage'
import ConfirmationModal from './ConfirmationModal'
import { useEnquiryLock } from '../stores/useLockStore'

const EnquiryDetailsView = NiceModal.create(({ initialTab = 0, enquiry, currentUser, refetchEnquiries }) => {
  const { cinfo, apir } = useContext(GlobalContext)
  const modalContainer = useRef(null)
  const [error, setError] = useState(null)
  const [tabIndex, setTabIndex] = useState(initialTab)
  const [modalClosed, setModalClosed] = useState(false)
  const [backorderDetails, setBackorderDetails] = useState(null)
  const [isLoadingBackorderDetails, setIsLoadingBackorderDetails] = useState(false)
  const { lockState, connected, unlock } = useEnquiryLock(enquiry.EnquiryId)

  // Fetch backorder details when modal opens
  // For enquiries with invoices, check BOTH:
  // 1. Invoice-based endpoint (items in invoice, can be partially backordered)
  // 2. Fully-backordered endpoint (items backordered before invoicing, not in invoice)
  useEffect(() => {
    const fetchBackorderDetails = async () => {
      setIsLoadingBackorderDetails(true)
      try {
        let invoiceResponse = null
        let fullyBackorderedResponse = null
        let combinedResponse = null

        const enquiryId = enquiry.Document || enquiry.EnquiryId

        // Always check invoice-based endpoint if invoice exists
        if (enquiry.Invoice) {
          try {
            invoiceResponse = await haxios.get(`/connectplus/backorder-status/${enquiry.Invoice}`)
          } catch (error) {
            console.error('Error fetching invoice backorder details:', error)
          }
        }

        // Also check fully-backordered endpoint (for items backordered before invoicing)
        if (enquiryId) {
          try {
            fullyBackorderedResponse = await haxios.get(`/connectplus/backorder-status/fully-backordered/${enquiryId}`)
          } catch (error) {
            console.error('Error fetching fully backordered details:', error)
          }
        }

        // Combine both responses if we have them
        if (invoiceResponse || fullyBackorderedResponse) {
          combinedResponse = {
            // Merge summaries - use invoice summary as base, add fully backordered counts
            summary: {
              ...invoiceResponse?.summary,
              // Add fully backordered summary fields
              totalBackorderDocuments: fullyBackorderedResponse?.summary?.totalBackorderDocuments || 0,
              totalBackorderQty: (invoiceResponse?.summary?.totalBackorderQty || 0) +
                (fullyBackorderedResponse?.summary?.totalBackorderQty || 0),
              totalBackorderValue: (invoiceResponse?.summary?.totalBackorderValue || 0) +
                (fullyBackorderedResponse?.summary?.totalBackorderValue || 0),
              // Combine totals
              totalLines: (invoiceResponse?.summary?.totalLines || 0) +
                (fullyBackorderedResponse?.summary?.totalLines || 0),
              // Update counts if fully backordered has items
              fullyBackordered: (invoiceResponse?.summary?.fullyBackordered || 0) +
                (fullyBackorderedResponse?.summary?.totalLines || 0)
            },
            // Combine line items from both responses
            lineItems: [
              ...(invoiceResponse?.lineItems || []),
              ...(fullyBackorderedResponse?.lineItems || [])
            ],
            // Combine backorder documents
            backorderDocuments: [
              ...(invoiceResponse?.backorderDocuments || []),
              ...(fullyBackorderedResponse?.backorderDocuments || [])
            ]
          }
        }

        setBackorderDetails(combinedResponse || invoiceResponse || fullyBackorderedResponse)
      } catch (error) {
        console.error('Error fetching backorder details:', error)
        setBackorderDetails(null)
      } finally {
        setIsLoadingBackorderDetails(false)
      }
    }

    fetchBackorderDetails()
  }, [enquiry.Invoice, enquiry.Document, enquiry.EnquiryId])

  const {
    isLoading: isLoadingLines,
    data: lines,
    refetch: refetchLines
  } = useApi({
    url: `https://api.aa-vision.com/api/connectplus/factor/response/${enquiry.EnquiryId}`
  })

  const {
    isLoading: isLoadingMessages,
    data: messages,
    refetch: refetchMessages
  } = useApi({
    url: `https://api.aa-vision.com/api/connectplus/factor/messages/${enquiry.EnquiryId}/true`,
    refetch: 20 * 1000,
    initial: []
  })

  const {
    isLoading: isLoadingEnq,
    data: enqData,
    refetch: refetchEnq
  } = useApi({
    url: `https://api.aa-vision.com/api/connectplus/factor/enquiry/${enquiry.EnquiryId}`,
    method: 'get',
    refetch: 5 * 1000,
    initial: enquiry
  })

  // Get can't supply reason for cancelled enquiries
  const {
    isLoading: isLoadingCantSupply,
    data: cantSupplyData,
    refetch: refetchCantSupply
  } = useApi({
    url: `${apir}/salesassist/cplusassist/cantsupply/${enquiry.EnquiryId}`,
    method: 'get',
    refetch: 5 * 1000,
    initial: null,
    enabled: enqData?.Status === 'Cancelled'
  })

  // Track initial status to detect changes
  const [initialStatus, setInitialStatus] = useState(null)

  // Set initial status when modal opens
  useEffect(() => {
    if (enquiry?.Status && !initialStatus) {
      setInitialStatus(enquiry.Status)
      console.log('Modal opened with initial status:', enquiry.Status, 'EnquiryId:', enquiry.EnquiryId)
    }
  }, [enquiry?.Status, initialStatus])

  // Monitor status changes and close modal on any change
  useEffect(() => {
    const currentStatus = enqData?.Status || enquiry?.Status
    console.log('Status check:', {
      enquiryId: enquiry.EnquiryId,
      currentStatus,
      initialStatus,
      changed: initialStatus && currentStatus && currentStatus !== initialStatus
    })

    // Close modal if status has changed from initial status (check both data sources)
    if (initialStatus && currentStatus && currentStatus !== initialStatus) {
      console.log('CLOSING MODAL - Status changed from', initialStatus, 'to', currentStatus)
      // Use the same close method as the close button to ensure unlock happens
      handleModalClose()
      refetchEnquiries() // Refresh the parent list
    }
  }, [enqData?.Status, enquiry?.Status, initialStatus])

  if (isLoadingLines) return null

  const handleTabChange = (event, newIndex) => {
    if (newIndex === 0) refetchLines()
    if (newIndex === 1) refetchMessages()
    setTabIndex(newIndex)
  }

  const isLockedByCurrentUser = lockState && lockState.username === currentUser
  const showUnlockButton = false //isLockedByCurrentUser || (lockedData.locked && cinfo.Admin)

  const handleUnlock = async () => {
    if (!lockState) return
    if (cinfo.Admin === false && isLockedByCurrentUser === false) return
    unlock()
    refetchEnquiries()
  }

  const messagesWithInitialImage = enqData.BlobStorageId
    ? [
      {
        BlobStorageId: enqData.BlobStorageId,
        Datetime: enqData.DatetimeCreated,
        EnquiryId: enqData.EnquiryId,
        Factor: false,
        FactorCompanyId: enqData.FactorCompanyId,
        Garage: true,
        MessageId: enqData.BlobStorageId,
        RecipientRead: false,
        Text: '',
        UserId: null,
        Username: null
      },
      ...messages
    ]
    : messages

  const handleModalClose = async () => {
    console.log('MODAL CLOSE CALLED:', enquiry?.EnquiryId)
    // Only auto-unlock if the current user holds the lock.
    if (isLockedByCurrentUser) {
      await handleUnlock()
    }
    modalContainer.current?.closeModal()
    NiceModal.hide(EnquiryDetailsView)
  }

  return (
    <ModalContainerAnimateDown ref={modalContainer} onClose={handleModalClose}>
      <Card sx={{ width: '900px' }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <EnquiryHeader
            handleModalClose={handleModalClose}
            apir={apir}
            enquiry={enqData}
            closeModal={modalContainer.current?.closeModal}
            refetchEnquiries={refetchEnquiries}
          />

          <Tabs value={tabIndex} onChange={handleTabChange}>
            <Tab label="Order Details" />
            <Tab label="Messages" />
          </Tabs>

          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {error && <Typography color="error">{error}</Typography>}
            {tabIndex === 0 && <DetailsTab lines={lines} apir={apir} enquiry={enquiry} cantSupplyData={cantSupplyData} backorderDetails={backorderDetails} isLoadingBackorderDetails={isLoadingBackorderDetails} />}
            {tabIndex === 1 && messages && (
              <MessagesTab
                enquiry={enqData}
                refetch={refetchMessages}
                messages={messagesWithInitialImage}
                locked={!!lockState}
                isLockedByCurrentUser={isLockedByCurrentUser}
              />
            )}
          </Box>
        </Box>
      </Card>
    </ModalContainerAnimateDown>
  )
})

const EnquiryHeader = ({ handleModalClose, enquiry, closeModal, handleUnlock, refetchEnquiries }) => {
  const { lockState, connected, unlock } = useEnquiryLock(enquiry.EnquiryId)
  const { cinfo, apir } = useContext(GlobalContext)

  const getStatusColor = (status) => {
    const colorMap = {
      'Waiting': '#FF6B35',
      'Order': '#E91E63',
      'Ordered': '#2ECC71',
      'Garage Confirmed': '#E91E63',
      'Quoted': '#3498DB',
      'Cancelled': '#E74C3C',
      'Archived': '#95A5A6'
    };
    return colorMap[status] || '#FF6B35';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'Waiting': 'TO QUOTE',
      'Ordered': 'ORDERED',
      'Quoted': 'QUOTED',
      'Order': 'DIRECT ORDER',
      'Garage Confirmed': 'CONFIRMED',
      'Cancelled': 'CANCELLED'
    };
    return labels[status] || status;
  };

  const statusColor = getStatusColor(enquiry.Status);

  // Clean debug logging
  console.log('EnquiryHeader render:', enquiry?.EnquiryId, enquiry?.Status)



  const isRegPlate = enquiry.Reg && enquiry.Reg !== 'CONSUMABLES'

  const today = dayjs().startOf('day')
  const createdDate = dayjs(enquiry.DatetimeCreated)
  const modifiedDate = dayjs(enquiry.DatetimeModified)

  const isCreatedToday = createdDate.isSame(today, 'day')
  const isModifiedToday = modifiedDate.isSame(today, 'day')

  const formattedCreatedDate = isCreatedToday
    ? 'Today, ' + createdDate.format('HH:mm')
    : createdDate.format('DD/MM/YYYY, HH:mm')
  const formattedModifiedDate = isModifiedToday
    ? 'Today, ' + modifiedDate.format('HH:mm')
    : modifiedDate.format('DD/MM/YYYY, HH:mm')

  const timeSinceCreated = createdDate.fromNow()
  const timeSinceModified = modifiedDate.fromNow()

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Status Bar */}
      <Box
        sx={{
          backgroundColor: statusColor,
          color: 'white',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: '48px'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1em' }}>
            {getStatusLabel(enquiry.Status)}
          </Typography>
          <Box sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            padding: '6px 16px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <Typography variant="h6" sx={{
              fontWeight: 700,
              fontSize: '1.1em',
              color: 'white'
            }}>
              {enquiry.Document}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ModalLockedChip lockState={lockState} />
          <IconButton onClick={handleModalClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Modal Content */}
      <Box sx={{ padding: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {`${enquiry.CustomerKeyCode} - ${enquiry.GarageName}`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ paddingRight: 2 }}>
              {isRegPlate ? <RegPlate size={1.05}>{enquiry.Reg}</RegPlate> : <ConsumablesBox />}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="h6" sx={{ lineHeight: 1 }}>
                {enquiry.Parts}
              </Typography>
              <Typography variant="caption">{enquiry.Notes || 'No additional notes provided.'}</Typography>
            </Box>
          </Box>
        </Box>


        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Box sx={{ lineHeight: 1 }}>
              <Typography variant="caption" sx={{ lineHeight: 1 }}>
                <span style={{ fontWeight: 600 }}>Created:</span> {formattedCreatedDate} ({timeSinceCreated})
              </Typography>
            </Box>
            <Box sx={{ lineHeight: 1 }}>
              <Typography variant="caption" sx={{ lineHeight: 1 }}>
                <span style={{ fontWeight: 600 }}>Modified:</span>{' '}
                {formattedModifiedDate === 'Invalid Date' ? 'Never' : formattedModifiedDate}{' '}
                {formattedModifiedDate === 'Invalid Date' ? '' : `(${timeSinceModified})`}
              </Typography>
            </Box>
          </Box>

          <Box>
            {false && (
              <Box sx={{ display: 'inline-block', paddingRight: 2 }}>
                <Button variant="text" startIcon={<LockOpenOutlined />} onClick={handleUnlock}>
                  Unlock
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const ModalLockedChip = ({ lockState }) => {
  const isLocked = !!lockState
  const username = lockState?.username

  return (
    <Box>
      <Tooltip title={`This enquiry is ${isLocked ? 'locked by ' + username : 'unlocked'}.`}>
        <Box
          sx={{
            background: isLocked ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            display: 'inline-flex',
            borderRadius: '12px',
            height: '28px',
            minWidth: '28px',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 8px',
            border: `1px solid rgba(255, 255, 255, 0.3)`,
            fontSize: '0.8em'
          }}
        >
          {isLocked ? <LockOutlined sx={{ fontSize: '16px' }} /> : <LockOpenOutlined sx={{ fontSize: '16px' }} />}
          {isLocked && username && (
            <Box sx={{ fontSize: '12px', fontWeight: 600, marginLeft: 0.5 }}>
              {username}
            </Box>
          )}
        </Box>
      </Tooltip>
    </Box>
  )
}

const LockedChip = ({ lockState }) => {
  const isLocked = !!lockState
  const username = lockState?.username

  return (
    <Box>
      <Tooltip title={`This enquiry is ${isLocked ? '' : 'un'}locked.`}>
        <Box
          sx={{
            background: isLocked ? 'orange' : 'green',
            color: 'white',
            display: 'inline-flex',
            borderRadius: '16px',
            height: '32px',
            minWidth: '32px',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 4px'
          }}
        >
          {isLocked ? <LockOutlined /> : <LockOpenOutlined />}
          {isLocked && <Box sx={{ fontSize: '14px', fontWeight: 600 }}>{username}</Box>}
        </Box>
      </Tooltip>
    </Box>
  )
}

const DetailsTab = ({ lines, apir, enquiry, cantSupplyData, backorderDetails, isLoadingBackorderDetails }) => {
  const handleCantSupplyClick = () => {
    NiceModal.show(ConfirmationModal, {
      enquiryId: lines?.[0]?.EnquiryId || enquiry?.EnquiryId,
      onCancelComplete: () => {
        //alert('Enquiry successfully cancelled')
      }
    })
  }

  if (enquiry?.Status === 'Waiting') {
    return (
      <Box sx={{ padding: 2, maxHeight: '50vh' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Parts Ordered
        </Typography>
        <Box sx={{
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          padding: '20px',
          backgroundColor: '#34495E',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <Typography variant="body1" sx={{ color: 'white' }}>Waiting for quote</Typography>
        </Box>
        <div className="mt-2" />
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Typography variant="body1">
            <strong>Created:</strong> {enquiry?.DatetimeCreated ? dayjs(enquiry.DatetimeCreated).format('DD/MM/YYYY, HH:mm') : 'N/A'}
          </Typography>
          {enquiry?.Status !== 'Cancelled' && (
            <Typography variant="body1">
              <Button
                onClick={handleCantSupplyClick}
                sx={{
                  backgroundColor: '#FF6B35',
                  color: 'white',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  borderRadius: '12px',
                  border: '2px solid #FF6B35',
                  boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
                  '&:hover': {
                    backgroundColor: '#FF6B35',
                    borderColor: '#FF6B35',
                    boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4)'
                  }
                }}
              >
                CAN'T SUPPLY
              </Button>
            </Typography>
          )}
        </div>
      </Box>
    )
  }

  // For quoted enquiries, show all parts (not just confirmed ones)
  // For other statuses, only show confirmed parts
  const shouldShowParts = lines && lines.length > 0 && (
    enquiry?.Status === 'Quoted' ||
    lines.filter(line => line.Confirmed === true).length > 0
  )

  const partsToShow = enquiry?.Status === 'Quoted'
    ? lines
    : lines?.filter(line => line.Confirmed === true) || []

  // Create a map of backorder items by part number for quick lookup
  const backorderMap = {}
  if (backorderDetails?.lineItems) {
    backorderDetails.lineItems.forEach(item => {
      const partNum = item.partNumber || item.PartNumber
      if (partNum) {
        backorderMap[partNum.toUpperCase()] = item
      }
    })
    // Debug: Only log for enquiry 22259
    if (enquiry.Document === 22259) {
      const backorderKeys = Object.keys(backorderMap)
      const orderParts = partsToShow.map(l => l.PartNumber).filter(Boolean)
      const missing = orderParts.filter(p => !backorderKeys.includes(p?.toUpperCase()))
      console.log('[22259] Part number matching:')
      console.log('  Order parts:', orderParts)
      console.log('  Backorder keys:', backorderKeys)
      console.log('  Missing from backorder:', missing)
      console.log('  Backorder items:', backorderKeys.map(k => ({
        key: k,
        status: backorderMap[k]?.status,
        partNumber: backorderMap[k]?.partNumber || backorderMap[k]?.PartNumber,
        backorderQty: backorderMap[k]?.backorderQty
      })))
    }
  }

  // Helper to get backorder status for a part
  const getBackorderForPart = (partNumber) => {
    if (!partNumber) return null
    return backorderMap[partNumber.toUpperCase()] || null
  }

  // Helper to get status color and info for individual line items
  // These labels refer to the LINE ITEM status, not the document status
  const getBackorderStatusInfo = (backorderItem) => {
    if (!backorderItem) return null

    const status = backorderItem.status
    switch (status) {
      case 'partially_backordered':
        // This line item has some quantity backordered
        return { color: '#F39C12', label: 'PARTIAL', icon: '⚠️' }
      case 'fully_backordered':
        // This line item is completely on backorder
        return { color: '#9B59B6', label: 'ON BACKORDER', icon: '🔄' }
      case 'fulfilled':
      default:
        // Don't show fulfilled status - only show backorder statuses
        return null
    }
  }

  return (
    <Box sx={{ padding: 2, maxHeight: '50vh' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {enquiry?.Status === 'Quoted' ? 'Quoted Parts' :
          lines && lines.filter(line => line.Confirmed === true).length > 0 ? 'Parts Ordered' : 'Enquiry Details'}
      </Typography>

      {shouldShowParts ? (
        <Box sx={{
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          padding: '0',
          backgroundColor: '#34495E',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'rgba(255, 107, 53, 0.1)' }}>
                <th style={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                  padding: '12px',
                  textAlign: 'left',
                  color: 'white',
                  fontWeight: '600'
                }}>Desc</th>
                <th style={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                  padding: '12px',
                  textAlign: 'left',
                  color: 'white',
                  fontWeight: '600'
                }}>Part Number</th>
                <th style={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                  padding: '12px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: '600'
                }}>Qty</th>
                <th style={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                  padding: '12px',
                  textAlign: 'right',
                  color: 'white',
                  fontWeight: '600'
                }}>Unit Price</th>
                <th style={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                  padding: '12px',
                  textAlign: 'right',
                  color: 'white',
                  fontWeight: '600'
                }}>Cost</th>
                {backorderDetails && backorderDetails.lineItems && backorderDetails.lineItems.length > 0 && (
                  <th style={{
                    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '12px',
                    textAlign: 'center',
                    color: 'white',
                    fontWeight: '600'
                  }}>Backorder Status</th>
                )}
              </tr>
            </thead>
            <tbody>
              {partsToShow.map((line) => {
                const backorderItem = getBackorderForPart(line.PartNumber)
                const statusInfo = getBackorderStatusInfo(backorderItem)
                // Check if item is backordered (partial or full) - ignore fulfilled
                const hasBackorder = backorderItem && statusInfo &&
                  (backorderItem.status === 'partially_backordered' || backorderItem.status === 'fully_backordered')

                return (
                  <tr key={line.ResponseDetailId} style={{
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    backgroundColor: hasBackorder && statusInfo?.color
                      ? `${statusInfo.color}15` // 15 hex = ~8% opacity
                      : 'transparent'
                  }}>
                    <td style={{
                      padding: '12px',
                      textAlign: 'left',
                      color: 'white'
                    }}>{line.ResponseDetailNotes}</td>
                    <td style={{
                      padding: '12px',
                      textAlign: 'left',
                      color: 'white',
                      fontWeight: hasBackorder ? '600' : 'normal'
                    }}>
                      {line.PartNumber}
                      {hasBackorder && (
                        <Box component="span" sx={{
                          ml: 1,
                          fontSize: '0.75em',
                          color: statusInfo?.color || '#F39C12'
                        }}>
                          ({backorderItem.backorderQty > 0 ? `${backorderItem.backorderQty} on ` : ''}backorder)
                        </Box>
                      )}
                    </td>
                    <td style={{
                      padding: '12px',
                      textAlign: 'center',
                      color: 'white'
                    }}>{line.Qty}</td>
                    <td style={{
                      padding: '12px',
                      textAlign: 'right',
                      color: 'white'
                    }}>
                      {line.UnitPrice !== undefined && line.UnitPrice !== null ? `£${line.UnitPrice.toFixed(2)}` : ''}
                    </td>
                    <td style={{
                      padding: '12px',
                      textAlign: 'right',
                      color: 'white'
                    }}>
                      {line.Cost !== undefined && line.Cost !== null ? `£${line.Cost.toFixed(2)}` : ''}
                    </td>
                    {backorderDetails && backorderDetails.lineItems && backorderDetails.lineItems.length > 0 && (
                      <td style={{
                        padding: '12px',
                        textAlign: 'center'
                      }}>
                        {statusInfo ? (
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            backgroundColor: statusInfo.color,
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '0.75em',
                            fontWeight: '600'
                          }}>
                            <span>{statusInfo.icon}</span>
                            {statusInfo.label}
                          </Box>
                        ) : (
                          <Box sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.875em' }}>
                            -
                          </Box>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Box>
      ) : (
        <Box sx={{
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          padding: '20px',
          backgroundColor: '#34495E',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <Typography variant="body1" sx={{ color: 'white' }}>
            {enquiry?.Status === 'Quoted' && (!lines || lines.length === 0)
              ? 'Quote provided - awaiting customer response'
              : 'No parts details available'}
          </Typography>
        </Box>
      )}

      {/* Backorder Details Section */}
      {backorderDetails && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
            Backorder Status
          </Typography>

          {/* Summary Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {/* Show different cards based on API type */}
            {backorderDetails.summary?.totalBackorderDocuments !== undefined ? (
              // Fully backordered API structure
              <>
                <Box sx={{
                  backgroundColor: '#9B59B6',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  minWidth: '120px',
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {backorderDetails.summary?.totalBackorderDocuments || 0}
                  </Typography>
                  <Typography variant="caption">Backorder Docs</Typography>
                </Box>
                <Box sx={{
                  backgroundColor: '#E74C3C',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  minWidth: '120px',
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {backorderDetails.summary?.totalLines || 0}
                  </Typography>
                  <Typography variant="caption">Total Lines</Typography>
                </Box>
                <Box sx={{
                  backgroundColor: '#3498DB',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  minWidth: '120px',
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {backorderDetails.summary?.totalBackorderQty || 0}
                  </Typography>
                  <Typography variant="caption">Backorder Qty</Typography>
                </Box>
              </>
            ) : (
              // Partial backorder API structure - only show backorder statuses, not fulfilled
              <>
                <Box sx={{
                  backgroundColor: '#F39C12',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  minWidth: '120px',
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {backorderDetails.summary?.partiallyBackordered || 0}
                  </Typography>
                  <Typography variant="caption">Partial</Typography>
                </Box>

                <Box sx={{
                  backgroundColor: '#9B59B6',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  minWidth: '120px',
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {backorderDetails.summary?.fullyBackordered || 0}
                  </Typography>
                  <Typography variant="caption">Full Backorder</Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {isLoadingBackorderDetails && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'white' }}>Loading backorder details...</Typography>
        </Box>
      )}

      {/* Cancellation Reason Display */}
      {enquiry?.Status === 'Cancelled' && (
        <Box sx={{
          mt: 2,
          p: 2,
          backgroundColor: 'rgba(231, 76, 60, 0.1)',
          border: '1px solid rgba(231, 76, 60, 0.3)',
          borderRadius: '12px'
        }}>
          <Typography variant="body2" sx={{ color: '#E74C3C', fontWeight: '600', mb: 1 }}>
            Cancellation Reason:
          </Typography>
          <Typography variant="body1" sx={{ color: 'white' }}>
            {cantSupplyData?.Reason ||
              enquiry?.ResponseHeaderNotes ||
              enquiry?.Notes ||
              lines?.[0]?.Notes ||
              lines?.[0]?.ResponseHeaderNotes ||
              'No reason provided'}
          </Typography>
        </Box>
      )}

      <div className="mt-2" />
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        {/* Delivery Date and Created Date */}
        {lines && lines[0]?.DeliveryDate && !backorderDetails && (
          <Typography variant="body1">
            <strong>Delivery Date:</strong> {dayjs(lines[0].DeliveryDate).format('DD/MM/YYYY')}
          </Typography>
        )}
        <Typography variant="body1">
          <strong>Created:</strong> {lines && lines[0] ? dayjs(lines[0].DatetimeCreated).format('DD/MM/YYYY, HH:mm') : 'N/A'}
        </Typography>
        {enquiry?.Status !== 'Cancelled' && (
          <Typography variant="body1">
            <Button
              onClick={handleCantSupplyClick}
              sx={{
                backgroundColor: '#FF6B35',
                color: 'white',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                borderRadius: '12px',
                border: '2px solid #FF6B35',
                boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
                '&:hover': {
                  backgroundColor: '#FF6B35',
                  borderColor: '#FF6B35',
                  boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4)'
                }
              }}
            >
              CAN'T SUPPLY
            </Button>
          </Typography>
        )}
      </div>
    </Box>
  )
}

const MessagesTab = ({ enquiry, messages, locked, refetch, isLockedByCurrentUser }) => {
  const messagesReversed = [...messages].reverse()

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '50vh' }}>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2, display: 'flex', flexDirection: 'column-reverse', px: 2 }}>
        {messages && messages.length === 0 && <Typography>No messages found for this enquiry.</Typography>}
        {messages &&
          messagesReversed.map((message, ind) => (
            <Message
              key={message.MessageId}
              message={message}
              prevMsg={messagesReversed[ind + 1]}
              nextMsg={messagesReversed[ind - 1]}
            />
          ))}
      </Box>

      {!locked || isLockedByCurrentUser ? (
        <ComposeMessage enqId={enquiry.EnquiryId} refetchMessages={refetch} />
      ) : (
        <Typography variant="body2" sx={{ textAlign: 'center', color: 'gray' }}>
          You cannot send messages on a locked enquiry.
        </Typography>
      )}
    </Box>
  )
}

const Message = ({ message, prevMsg, nextMsg }) => {
  const [imageUrl, setImageUrl] = useState(null)
  const hasImage = !!message.BlobStorageId
  const hasText = !!message.Text
  const prevIsSameSender = prevMsg ? prevMsg.Factor === message.Factor && prevMsg.Garage === message.Garage : false
  const nextIsSameSender = nextMsg ? nextMsg.Factor === nextMsg.Factor && nextMsg.Garage === message.Garage : false

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const url = await getImageFromBlobId(message.BlobStorageId)
        setImageUrl(url)
      } catch (e) {
        console.error('Error fetching image:', e)
        setImageUrl('')
      }
    }

    if (imageUrl) return
    fetchImage()
  }, [message.BlobStorageId])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: message.Factor ? 'flex-end' : 'flex-start',
        mb: '3px'
      }}
    >
      <Box
        sx={{
          maxWidth: '70%',
          py: 1,
          px: 1.5,
          borderRadius: 3,
          backgroundColor: message.Factor ? 'dodgerblue' : '#e0e0e0',
          color: message.Factor ? 'white' : 'black',
          ...(message.Factor && !nextIsSameSender ? { borderBottomRightRadius: 0 } : {}),
          ...(message.Garage && !nextIsSameSender ? { borderBottomLeftRadius: 0 } : {})
        }}
      >
        {!!message.Username && prevIsSameSender === false && (
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0 }}>
            {message.Username}
          </Typography>
        )}
        {hasImage && (
          <div
            className="rounded-md w-[250px] h-[250px] bg-contain bg-no-repeat bg-center border border-gray-300 flex items-center justify-center"
            style={{ backgroundColor: 'white', backgroundImage: `url(${imageUrl || '/img/whitepixel.png'})` }}
            onClick={() => (imageUrl ? NiceModal.show('lightboxModal', { url: imageUrl }) : null)}
          >
            {!imageUrl && <Spinner />}
          </div>
        )}
        {hasText && <Typography variant="body1">{message.Text}</Typography>}

        <Typography variant="caption" sx={{ display: 'block', mt: 0, textAlign: 'right', color: 'rgba(0,0,0,0.6)' }}>
          <div className="inline-flex items-center">
            {message.Datetime
              ? dayjs(message.Datetime).isSame(dayjs(), 'day')
                ? dayjs(message.Datetime).format('HH:mm')
                : dayjs(message.Datetime).format('DD/MM/YYYY HH:mm')
              : 'Unknown date'}
            {message.Factor &&
              (message.RecipientRead ? (
                <Icon fontSize="small" sx={{ marginLeft: 0.5 }}>
                  done_all
                </Icon>
              ) : (
                <Icon fontSize="small" sx={{ marginLeft: 0.5 }}>
                  done
                </Icon>
              ))}
          </div>
        </Typography>
      </Box>
    </Box>
  )
}

export default EnquiryDetailsView
